import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { StateError } from '~/errors/StateError'
import { useInteractionControlStore } from '~/stores/interactionControl'
import type { CallParticipant } from '~/types/VoiceCall'
// import { VoiceProvider } from '~/types/Voice'

export const useVoiceCallStore = defineStore('voiceCall', () => {
  // OTHER STORES ====================================
  const interactionControllerStore = useInteractionControlStore()
  const { interactionState, simulation } = storeToRefs(
    interactionControllerStore,
  )

  // SIM & CHARACTER ====================================
  const character = computed(() => {
    return simulation.value?.character ?? null
  })

  const characterFullName = computed(() => {
    if (!simulation.value) return null

    if (simulation.value.usePrimaryCharacter && character.value) {
      return character.value.fullName
    }

    return simulation.value.manualCharacterFullName ?? null
  })

  const characterAvatarUrl = computed(() => {
    if (!simulation.value) return null

    if (simulation.value.usePrimaryCharacter && character.value) {
      return character.value.avatarUrl ?? null
    }

    return null
  })

  const voiceCallProvider = computed(() => {
    if (simulation.value?.usePrimaryCharacter) {
      return simulation.value.character.voiceProvider ?? null
    }

    return simulation.value?.manualCharacterVoiceProvider ?? null
  })

  const voiceCallProviderId = computed(() => {
    // return 'agent_01jx2cb2e5fk8te343qe4n5fjx'
    return simulation.value?.voiceCallProviderId ?? null
  })

  // PARTICIPANTS ====================================
  const participants = ref<Map<string, CallParticipant>>(new Map())

  const activeSpeaker = ref<'candidate' | 'character' | null>(null)

  function addCharacter() {
    if (!characterFullName.value) return

    participants.value.set('character', {
      fullName: characterFullName.value,
      avatarUrl: characterAvatarUrl.value ?? '',
    })
  }

  function removeCharacter() {
    participants.value.delete('character')
    if (activeSpeaker.value === 'character') {
      activeSpeaker.value = null
    }
  }

  function ensureCharacterJoined() {
    if (!participants.value.has('character')) {
      addCharacter()
    }
  }

  // CAPTIONS ====================================
  const isCaptionsEnabled = useLocalStorage('isCaptionsEnabled', false)
  const captions = ref<string[]>([])

  function setCaption(text: string) {
    captions.value.push(text)

    const readingTime = Math.max(text.split(' ').length * 0.6 * 1000, 3000)

    setTimeout(() => {
      const index = captions.value.findIndex((c) => c === text)
      if (index !== -1) {
        captions.value.splice(index, 1)
      }
    }, readingTime)
  }

  // META STATE ====================================
  const hasError = ref(false)

  function initVoiceCallStore() {
    if (!interactionControllerStore.isInteractionReady) return

    $reset()

    participants.value.set('candidate', {
      fullName: 'You',
      avatarUrl: '',
    })

    if (!voiceCallProviderId.value) {
      interactionControllerStore.postError('This simulation is not ready.')
      hasError.value = true
      console.error(
        'The simulation could not be started because the simulation has no voice call provider ID.',
      )
    }
  }

  async function startCall() {
    if (!voiceCallProviderId.value) {
      interactionControllerStore.postError('This simulation is not ready.')
      hasError.value = true
      captureError(
        new StateError(
          'The simulation could not be started because the simulation has no voice call provider ID.',
        ),
      )
      return
    }

    const { error } = await interactionControllerStore.startInteraction()

    if (error !== null) {
      hasError.value = true
      interactionControllerStore.postError(error.message)
      return
    }

    const conversationId = await startVoiceCall(voiceCallProviderId.value)

    if (!conversationId) {
      interactionControllerStore.postError('The call could not be started.')
      hasError.value = true
      captureError(
        new StateError(
          'The call could not be started because no conversation ID was found.',
        ),
      )
      return
    }

    const { error: setProviderInteractionIdError } =
      await interactionControllerStore.setProviderInteractionId(conversationId)

    if (setProviderInteractionIdError !== null) {
      interactionControllerStore.postError(
        'The interaction could not be started.',
      )
      hasError.value = true
      endCall(true)
    }
  }

  async function endCall(silent: boolean = false) {
    removeCharacter()
    endVoiceCall()

    const { error } = await interactionControllerStore.forceComplete(silent)

    if (error !== null) {
      interactionControllerStore.postError(
        'Interaction could not be completed.',
      )
      hasError.value = true
    }
  }

  function $reset() {
    hasError.value = false
    captions.value = []
    participants.value = new Map()
  }

  // VOICE CALL =================================================================================
  const {
    startCall: startVoiceCall,
    endCall: endVoiceCall,
    userMicrophoneLevel,
  } = useVoiceCall(voiceCallProvider, {
    onAgentTranscript: (transcript: string) =>
      isCaptionsEnabled.value && setCaption(transcript),
    onUserStartedSpeaking: () => {
      activeSpeaker.value = 'candidate'
    },
    onUserStoppedSpeaking: () => {
      activeSpeaker.value = null
    },
    onAgentStartedSpeaking: () => {
      ensureCharacterJoined()
      activeSpeaker.value = 'character'
    },
    onAgentStoppedSpeaking: () => {
      activeSpeaker.value = null
    },
    onHangup: (endedBy: string) => {
      console.info(`Conversation ended by ${endedBy}`)
      if (endedBy !== 'user') {
        endCall()
      }
    },
    onError: (err: any) => {
      if (err.isFatal === undefined || err.isFatal) {
        interactionControllerStore.postError(err.description)
        hasError.value = true
      }
    },
  })

  return {
    $reset,
    hasError,
    startCall,
    interactionState,
    simulation,
    character,
    characterFullName,
    characterAvatarUrl,
    voiceCallProvider,
    voiceCallProviderId,
    participants,
    activeSpeaker,
    isCaptionsEnabled,
    captions,
    setCaption,
    initVoiceCallStore,
    endCall,
    userMicrophoneLevel,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useVoiceCallStore, import.meta.hot))
}
